import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from '@hello-pangea/dnd';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { CampaignFilterPill } from '@/components/CampaignFilterPill';
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal';
import { GateStatusBadge } from '@/components/GateStatusBadge';

interface Opportunity {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: string;
  currency: string;
  forecastClassification: string;
  progressPercent: string;
  currentStageId: string;
  templateId: string;
  accountId: string;
  assignedRepId: string | null;
}

interface Stage {
  id: string;
  order: number;
  name: string;
  percentageWeight: string;
}

interface Template {
  id: string;
  name: string;
  stages: Stage[];
}

const FORECAST_COLORS: Record<string, string> = {
  COMMIT: 'border-l-emerald-500',
  BEST_CASE: 'border-l-blue-500',
  UPSIDE: 'border-l-amber-500',
  PIPELINE: 'border-l-zinc-400',
};

const FORECAST_BADGES: Record<string, string> = {
  COMMIT: 'bg-emerald-900/40 text-emerald-200',
  BEST_CASE: 'bg-blue-900/40 text-blue-200',
  UPSIDE: 'bg-amber-900/40 text-amber-200',
  PIPELINE: 'bg-muted text-foreground',
};

type StatusFilter = 'OPEN' | 'ALL' | 'WON' | 'LOST';

export function OpportunitiesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // Track which template's stages are currently loaded so we can reload when
  // an opp uses a different (newer) template — avoids the bug where a closed
  // opp on an old template locks the kanban into wrong stage columns.
  const loadedTemplateId = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (campaignId) params.campaignId = campaignId;
      const { data: opportunities } = await apiClient.get<Opportunity[]>(
        '/opportunities',
        { params },
      );
      setOpps(opportunities);

      // Use the first OPEN opp's template (ignoring closed opps on old templates).
      const firstOpen = opportunities.find((o) => o.status === 'OPEN');
      if (firstOpen) {
        if (firstOpen.templateId !== loadedTemplateId.current) {
          const { data: template } = await apiClient.get<Template>(
            `/process-templates/${firstOpen.templateId}`,
          );
          setStages(template.stages.sort((a, b) => a.order - b.order));
          loadedTemplateId.current = firstOpen.templateId;
        }
      } else if (loadedTemplateId.current === null) {
        // No open opps and no stages loaded yet: fall back to the tenant's
        // template list (server returns active/newest first).
        const { data: templates } = await apiClient.get<Template[]>(
          '/process-templates',
        );
        if (templates.length > 0) {
          const { data: template } = await apiClient.get<Template>(
            `/process-templates/${templates[0].id}`,
          );
          setStages(template.stages.sort((a, b) => a.order - b.order));
          loadedTemplateId.current = templates[0].id;
        }
      }
    } catch {
      setOpps([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const oppsByStage = new Map<string, Opportunity[]>();
  for (const stage of stages) {
    oppsByStage.set(stage.id, []);
  }
  const closedOpps: Opportunity[] = [];
  for (const opp of opps) {
    if (opp.status === 'WON' || opp.status === 'LOST') {
      closedOpps.push(opp);
    } else {
      const bucket = oppsByStage.get(opp.currentStageId);
      if (bucket) {
        bucket.push(opp);
      } else {
        const firstStage = stages[0];
        if (firstStage) {
          oppsByStage.get(firstStage.id)?.push(opp);
        }
      }
    }
  }

  for (const bucket of oppsByStage.values()) {
    bucket.sort(
      (a, b) => parseFloat(b.estimatedDealValue) - parseFloat(a.estimatedDealValue),
    );
  }

  async function handleDragEnd(result: DropResult) {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const opp = opps.find((o) => o.id === draggableId);
    if (!opp) return;

    const targetStageId = destination.droppableId;

    setOpps((prev) =>
      prev.map((o) =>
        o.id === draggableId ? { ...o, currentStageId: targetStageId } : o,
      ),
    );

    try {
      await apiClient.post(`/opportunities/${draggableId}/advance`, {
        targetStageId,
      });
    } catch {
      load();
    }
  }

  const filters: StatusFilter[] = ['OPEN', 'ALL', 'WON', 'LOST'];

  if (loading && opps.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{t('nav.opportunities')}</h1>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CreateOpportunityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">{t('nav.opportunities')}</h1>
          <CampaignFilterPill />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowCreate(true)}>
            + Opportunity
          </Button>
          {filters.map((f) => (
            <Button
              key={f}
              variant={statusFilter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f)}
            >
              {f}
            </Button>
          ))}
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {stages.map((stage) => {
            const items = oppsByStage.get(stage.id) ?? [];
            return (
              <Droppable key={stage.id} droppableId={stage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex w-72 min-w-[18rem] flex-shrink-0 flex-col rounded-lg border bg-muted/50 ${
                      snapshot.isDraggingOver ? 'ring-2 ring-primary/30' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between border-b px-3 py-2">
                      <span className="text-sm font-medium">{stage.name}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {items.length}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2 p-2" style={{ minHeight: 80 }}>
                      {items.map((opp, index) => (
                        <Draggable
                          key={opp.id}
                          draggableId={opp.id}
                          index={index}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <Link
                              to={`/opportunities/${opp.id}`}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`block rounded-md border border-l-4 bg-card p-3 shadow-sm transition-shadow hover:shadow-md ${
                                FORECAST_COLORS[opp.forecastClassification] ?? 'border-l-zinc-400'
                              } ${dragSnapshot.isDragging ? 'shadow-lg ring-2 ring-primary/40' : ''}`}
                            >
                              <div className="mb-1 text-sm font-medium leading-tight">
                                {opp.name}
                              </div>
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                  {(() => {
                                    const curIdx = stages.findIndex((s) => s.id === opp.currentStageId);
                                    const next = stages[curIdx + 1];
                                    return next ? (
                                      <GateStatusBadge opportunityId={opp.id} targetStageId={next.id} />
                                    ) : null;
                                  })()}
                                  {opp.currency}{' '}
                                  {parseFloat(opp.estimatedDealValue).toLocaleString()}
                                </span>
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                    FORECAST_BADGES[opp.forecastClassification] ?? 'bg-muted text-foreground'
                                  }`}
                                >
                                  {opp.forecastClassification}
                                </span>
                              </div>
                            </Link>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}

          {(statusFilter === 'ALL' || statusFilter === 'WON' || statusFilter === 'LOST') &&
            closedOpps.length > 0 && (
              <div className="flex w-72 min-w-[18rem] flex-shrink-0 flex-col rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <span className="text-sm font-medium">Closed</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {closedOpps.length}
                  </span>
                </div>
                <div className="flex-1 space-y-2 p-2" style={{ minHeight: 80 }}>
                  {closedOpps.map((opp) => (
                    <Link
                      key={opp.id}
                      to={`/opportunities/${opp.id}`}
                      className="block rounded-md border bg-card p-3 opacity-70"
                    >
                      <div className="mb-1 text-sm font-medium leading-tight">
                        {opp.name}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {opp.currency}{' '}
                          {parseFloat(opp.estimatedDealValue).toLocaleString()}
                        </span>
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                            opp.status === 'WON'
                              ? 'bg-emerald-900/40 text-emerald-200'
                              : 'bg-red-900/40 text-red-200'
                          }`}
                        >
                          {opp.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
        </div>
      </DragDropContext>
    </div>
  );
}
